import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  LayoutDashboard, Tags, Layers, UtensilsCrossed, Activity, History, FileBarChart, Users, Settings,
  Wallet, ClipboardList, ChefHat, ClipboardCheck, LogOut, Menu as MenuIcon, KeyRound, User,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/api";

const ADMIN_NAV = [
  { group: "Utama", items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }] },
  { group: "Master Menu", items: [
    { to: "/admin/kategori", label: "Kategori Menu", icon: Tags },
    { to: "/admin/sub-kategori", label: "Sub Kategori Menu", icon: Layers },
    { to: "/admin/menu", label: "Buat Menu", icon: UtensilsCrossed },
  ] },
  { group: "Transaksi", items: [
    { to: "/admin/transaksi-berjalan", label: "Transaksi Berjalan", icon: Activity },
    { to: "/admin/riwayat-transaksi", label: "Riwayat Transaksi", icon: History },
  ] },
  { group: "Laporan", items: [{ to: "/admin/laporan", label: "Laporan Transaksi", icon: FileBarChart }] },
  { group: "Pengaturan", items: [
    { to: "/admin/akun", label: "Pengaturan Akun", icon: Users },
    { to: "/admin/aplikasi", label: "Pengaturan Aplikasi", icon: Settings },
  ] },
];

const ROLE_NAV = {
  kasir: [
    { to: "/kasir", label: "Dashboard", icon: Wallet, end: true },
    { to: "/kasir/riwayat", label: "Riwayat Transaksi", icon: History },
    { to: "/kasir/laporan", label: "Laporan Transaksi", icon: FileBarChart },
  ],
  waiter: [{ to: "/waiter", label: "Open Bill", icon: ClipboardList }],
  dapur: [{ to: "/dapur", label: "Dashboard Dapur", icon: ChefHat }],
  checker: [{ to: "/checker", label: "Dashboard Checker", icon: ClipboardCheck }],
};

const TopLink = ({ item }) => (
  <NavLink to={item.to} end={item.end ?? false}
    data-testid={`topnav-${item.to.replace(/\//g, "-").slice(1)}`}
    className={({ isActive }) => `flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-orange-600 text-white" : "text-stone-300 hover:bg-white/10 hover:text-white"}`}>
    <item.icon className="h-4 w-4" />{item.label}
  </NavLink>
);

function TopNav() {
  const { user, logout, settings } = useAuth();
  const navigate = useNavigate();
  const items = [...ROLE_NAV[user.role], { to: "/akun", label: "Akun Saya", icon: KeyRound }];
  return (
    <header className="sticky top-0 z-30 bg-[#1C1410] text-white shadow-md" data-testid="top-nav">
      <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 py-2.5 md:px-8">
        <img src="/icon-192.png" alt="GoMenu" className="h-8 w-8 rounded-lg" />
        <span className="mr-2 hidden text-lg font-extrabold tracking-tight sm:block" data-testid="brand-name">{settings?.app_name || "GoMenu"}</span>
        <div className="ml-auto flex items-center gap-3">
          <div className="hidden text-right md:block">
            <p className="text-sm font-semibold leading-tight" data-testid="current-user-name">{user.name}</p>
            <p className="text-[11px] text-orange-400" data-testid="current-user-role">{ROLE_LABEL[user.role]}</p>
          </div>
          <span className="grid h-8 w-8 place-items-center rounded-full bg-white/10 md:hidden"><User className="h-4 w-4" /></span>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-sm text-stone-200 hover:bg-white/10">
            <LogOut className="h-4 w-4" /><span className="hidden sm:inline">Keluar</span>
          </button>
        </div>
      </div>
      <nav className="mx-auto flex max-w-[1400px] gap-1 overflow-x-auto px-4 pb-2.5 md:px-8">
        {items.map((i) => <TopLink key={i.to} item={i} />)}
      </nav>
    </header>
  );
}

const NavItem = ({ item, onClick }) => (
  <NavLink to={item.to} end={item.end} onClick={onClick}
    data-testid={`nav-${item.to.replace(/\//g, "-").slice(1) || "home"}`}
    className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-orange-600 text-white shadow-sm" : "text-stone-300 hover:bg-white/5 hover:text-white"}`}>
    <item.icon className="h-4 w-4 shrink-0" />{item.label}
  </NavLink>
);

function SideNav({ onNavigate }) {
  const { user, logout, settings } = useAuth();
  const navigate = useNavigate();
  const groups = ADMIN_NAV;
  return (
    <div className="flex h-full flex-col bg-[#1C1410] text-white">
      <div className="flex items-center gap-3 px-5 py-5">
        <img src="/icon-192.png" alt="GoMenu" className="h-9 w-9 rounded-xl" />
        <div className="min-w-0">
          <p className="truncate text-lg font-extrabold tracking-tight" data-testid="brand-name">{settings?.app_name || "GoMenu"}</p>
          <p className="text-[11px] uppercase tracking-widest text-orange-400">Resto & Cafe</p>
        </div>
      </div>
      <nav className="flex-1 space-y-5 overflow-y-auto px-3 pb-4">
        {groups.map((g) => (
          <div key={g.group}>
            <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">{g.group}</p>
            <div className="space-y-0.5">{g.items.map((i) => <NavItem key={i.to} item={i} onClick={onNavigate} />)}</div>
          </div>
        ))}
      </nav>
      <div className="border-t border-white/10 p-3">
        <div className="mb-2 px-2">
          <p className="truncate text-sm font-semibold" data-testid="current-user-name">{user.name}</p>
          <p className="text-xs text-orange-400" data-testid="current-user-role">{ROLE_LABEL[user.role]}</p>
        </div>
        <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-stone-300 hover:bg-white/5 hover:text-white">
          <LogOut className="h-4 w-4" /> Keluar
        </button>
      </div>
    </div>
  );
}

export default function AppShell() {
  const [open, setOpen] = useState(false);
  const { user, settings } = useAuth();
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen bg-stone-50">
        <TopNav />
        <main className="mx-auto max-w-[1400px] p-4 md:p-8"><Outlet /></main>
        {user.role === "kasir" && (
          <footer data-testid="app-footer" className="border-t border-stone-200 py-5 text-center text-xs text-zinc-500">
            Copyright &copy; {new Date().getFullYear()} GoMenu
          </footer>
        )}
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-stone-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block"><SideNav /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-0 p-0 [&>button]:text-white [&>button]:opacity-90"><SideNav onNavigate={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-stone-200 bg-white/80 px-4 py-3 backdrop-blur-lg lg:hidden">
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="rounded-lg p-2 hover:bg-stone-100"><MenuIcon className="h-5 w-5" /></button>
        <img src="/icon-192.png" alt="" className="h-7 w-7 rounded-lg" />
        <span className="font-extrabold tracking-tight">{settings?.app_name || "GoMenu"}</span>
      </header>
      <main className="lg:pl-64">
        <div className="mx-auto max-w-[1400px] p-4 md:p-8"><Outlet /></div>
      </main>
    </div>
  );
}
